/**
 * Site-wide banner strip — reads active banners from the public
 * `banners/list` endpoint and renders them above the layout chrome.
 *
 * Backend pair: nodejs_server/controllers/BannersController.js — authored
 * in the admin panel at /banners; persisted in
 * storage/app/settings.json under the `admin_banners` key.
 *
 * Behavior
 *   - Fetches with the current user context (logged-in flag, country,
 *     plan) so the backend audience filter narrows the response before
 *     it reaches the browser. Backend does the filtering; we also
 *     defensively re-filter client-side in case the cache serves a
 *     stale response with a broader set.
 *   - Dismissible banners set `sessionStorage['banner-dismissed:<id>']`
 *     so the same banner doesn\'t re-appear on tab-internal navigations,
 *     but DOES re-appear in a fresh session. Matches
 *     [[feedback-dismiss-persistence]] — sessionStorage over local.
 *   - Multiple active banners stack; user can dismiss each independently.
 *   - Renders NOTHING if the fetch fails, the response is empty, or all
 *     banners are already dismissed — every layout can mount this
 *     unconditionally.
 */
import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

import { api } from '@/lib/api';
import { sanitizeHtml } from '@/lib/sanitizeHtml';
import { useAuth } from '@/pages/auth';

interface Banner {
  id: string;
  message: string;
  style: 'info' | 'warning' | 'error' | 'success';
  audience: { type: string; value?: string | null };
  dismissible: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
}
interface Response { banners: Banner[] }

const DISMISS_KEY_PREFIX = 'banner-dismissed:';

const STYLE_MAP: Record<Banner['style'], {
  wrapper: string;
  icon: ReactElement;
  iconWrap: string;
}> = {
  info: {
    wrapper: 'border-blue-200 bg-blue-50 text-blue-900',
    icon: <Info className="h-4 w-4" aria-hidden="true" />,
    iconWrap: 'text-blue-600',
  },
  warning: {
    wrapper: 'border-amber-200 bg-amber-50 text-amber-900',
    icon: <AlertTriangle className="h-4 w-4" aria-hidden="true" />,
    iconWrap: 'text-amber-600',
  },
  error: {
    wrapper: 'border-red-200 bg-red-50 text-red-900',
    icon: <AlertCircle className="h-4 w-4" aria-hidden="true" />,
    iconWrap: 'text-red-600',
  },
  success: {
    wrapper: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />,
    iconWrap: 'text-emerald-600',
  },
};

function readDismissed(): Set<string> {
  try {
    const out = new Set<string>();
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(DISMISS_KEY_PREFIX)) out.add(k.slice(DISMISS_KEY_PREFIX.length));
    }
    return out;
  } catch {
    return new Set();
  }
}

export function SiteBanner(): ReactElement | null {
  const user = useAuth((s) => s.user);
  const status = useAuth((s) => s.status);
  const logged = status === 'authenticated';
  const country = (user?.country || '').trim();
  const plan = ((user as { plan?: string } | null | undefined)?.plan || '').trim();

  // sessionStorage-backed dismiss set. useState-seeded so a dismiss during
  // this session hides the banner without needing a Query refetch.
  const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissed());

  const params = new URLSearchParams();
  if (logged) params.set('logged', '1');
  if (country) params.set('country', country);
  if (plan) params.set('plan', plan);
  const qs = params.toString();

  const query = useQuery({
    queryKey: ['banners', 'list', { logged, country, plan }],
    queryFn: () => api.get<Response>(`banners/list${qs ? `?${qs}` : ''}`),
    // Match the backend cache TTL — no point hammering the server more
    // often than it invalidates. New banners still appear within 60s.
    staleTime: 60_000,
    // Refetch quietly on window focus so a returning user picks up a
    // banner that landed while they were away.
    refetchOnWindowFocus: true,
    // Fail-quiet — a broken banners API must not take the site down.
    retry: 1,
  });

  const visible = useMemo(() => {
    const list = query.data?.banners ?? [];
    return list.filter((b) => !dismissed.has(b.id));
  }, [query.data, dismissed]);

  if (visible.length === 0) return null;

  function handleDismiss(id: string) {
    try { sessionStorage.setItem(DISMISS_KEY_PREFIX + id, '1'); } catch { /* private mode */ }
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-0.5" data-testid="site-banners">
      {visible.map((b) => {
        const s = STYLE_MAP[b.style] ?? STYLE_MAP.info;
        return (
          <div
            key={b.id}
            role="alert"
            aria-live="polite"
            className={`flex items-center gap-2 border-b px-3 py-2 text-sm ${s.wrapper}`}
          >
            <span className={s.iconWrap}>{s.icon}</span>
            {/*
             * Banner messages support a small allowlist of HTML (b, strong,
             * i, em, u, br, a, span, etc.) so admins can bold key words,
             * link to a landing page, or wrap a phrase in a color span.
             * Everything is filtered through the shared `sanitizeHtml`
             * allowlist that the blog + page renderers already use —
             * scripts, event handlers, style/link/iframe, and unsafe URL
             * schemes are stripped so a bad banner can\'t inject XSS.
             */}
            <span
              className="site-banner-message flex-1 break-words [&_a]:underline [&_a]:font-medium"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(b.message) }}
            />
            {b.dismissible ? (
              <button
                type="button"
                onClick={() => handleDismiss(b.id)}
                aria-label="Dismiss banner"
                className="ml-2 rounded p-1 opacity-70 hover:bg-black/5 hover:opacity-100"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default SiteBanner;
